import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

const API_BASE = '/api'

interface Job {
  id: string
  job_id: string
  title: string
  department: string
  location: string
  status: string
  applications_count: number
  salary_min: number
  salary_max: number
}

interface Application {
  id: string
  job_title: string
  candidate_name: string
  email: string
  status: string
  experience_years: number
  rating: number
}

export default function RecruitmentPage() {
  const [jobs, setJobs] = useState<Job[]>([])
  const [applications, setApplications] = useState<Application[]>([])
  const [statistics, setStatistics] = useState<any>(null)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    const [jobsRes, appsRes, statsRes] = await Promise.all([
      fetch(API_BASE + '/recruitment/jobs'),
      fetch(API_BASE + '/recruitment/applications'),
      fetch(API_BASE + '/recruitment/statistics')
    ])
    if (jobsRes.ok) setJobs((await jobsRes.json()).jobs || [])
    if (appsRes.ok) setApplications((await appsRes.json()).applications || [])
    if (statsRes.ok) setStatistics(await statsRes.json())
  }

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'open': case 'hired': return 'bg-green-100 text-green-800'
      case 'screening': case 'interview': return 'bg-blue-100 text-blue-800'
      case 'offer': return 'bg-purple-100 text-purple-800'
      case 'closed': case 'filled': case 'rejected': return 'bg-red-100 text-red-800'
      case 'on_hold': case 'new': return 'bg-yellow-100 text-yellow-800'
      default: return 'bg-gray-100 text-gray-800'
    }
  }

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 }).format(amount)
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold">Recruitment</h1>
          <button className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">+ Post Job</button>
        </div>

        {statistics && (
          <div className="grid grid-cols-5 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Open Positions</div>
              <div className="text-2xl font-bold">{statistics.open_positions}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Total Applications</div>
              <div className="text-2xl font-bold text-blue-600">{statistics.total_applications}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">In Interview</div>
              <div className="text-2xl font-bold text-purple-600">{statistics.in_interview}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Offers Extended</div>
              <div className="text-2xl font-bold text-green-600">{statistics.offers_extended}</div>
            </div>
            <div className="bg-white rounded-lg shadow p-4">
              <div className="text-sm text-gray-500">Acceptance Rate</div>
              <div className="text-2xl font-bold">{statistics.offer_acceptance_rate}%</div>
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-6">
          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Job Postings</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Applicants</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {jobs.slice(0, 10).map(job => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{job.title}</div>
                      <div className="text-xs text-gray-500">{job.department} • {job.location}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="font-bold text-blue-600">{job.applications_count}</div>
                      <div className="text-xs text-gray-500">{formatCurrency(job.salary_min)} - {formatCurrency(job.salary_max)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(job.status)}`}>{job.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="bg-white rounded-lg shadow">
            <div className="px-6 py-4 border-b"><h2 className="text-xl font-semibold">Recent Applications</h2></div>
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Candidate</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {applications.slice(0, 10).map(app => (
                  <tr key={app.id} className="hover:bg-gray-50">
                    <td className="px-6 py-4">
                      <div className="font-medium">{app.candidate_name}</div>
                      <div className="text-xs text-gray-500">{app.experience_years} yrs exp</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="text-sm">{app.job_title}</div>
                      <div className="text-yellow-500 text-xs">{'★'.repeat(app.rating)}{'☆'.repeat(5 - app.rating)}</div>
                    </td>
                    <td className="px-6 py-4">
                      <span className={`px-2 py-1 rounded text-xs ${getStatusColor(app.status)}`}>{app.status}</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </main>
    </div>
  )
}
